import { useState, useEffect, useCallback } from 'react';
import automationApi from '../services/automationApi';
import { toast } from 'react-toastify';
import { PAGINATION_DEFAULTS } from '../constants';

const useExecutions = () => {
  const [executions, setExecutions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    timeframe: '',
    ruleId: '',
    page: PAGINATION_DEFAULTS.PAGE,
    limit: PAGINATION_DEFAULTS.LIMIT,
  });
  const [totalItems, setTotalItems] = useState(0);

  const fetchExecutions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.timeframe) {
        const now = new Date();
        const ms = { '1h': 3600000, '24h': 86400000, '7d': 604800000, '30d': 2592000000 };
        if (ms[filters.timeframe]) {
          params.startDate = new Date(now.getTime() - ms[filters.timeframe]).toISOString();
          params.endDate = now.toISOString();
        }
      }
      if (filters.ruleId) params.automationId = filters.ruleId;
      params.page = filters.page;
      params.perPage = filters.limit;

      const result = await automationApi.getExecutions(params);
      setExecutions(result.data || []);
      setTotalItems(result.count || 0);
    } catch (err) {
      setError(err.message);
      setExecutions([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchExecutions();
  }, [fetchExecutions]);

  const updateFilters = useCallback((updates) => {
    setFilters(prev => ({ ...prev, page: 1, ...updates }));
  }, []);

  const goToPage = useCallback((page) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  const retryExecution = useCallback(async (id) => {
    try {
      await automationApi.retryExecution(id);
      toast.success('Execution retry triggered');
      fetchExecutions();
    } catch (err) {
      toast.error('Failed to retry execution');
    }
  }, [fetchExecutions]);

  const refresh = useCallback(() => {
    fetchExecutions();
  }, [fetchExecutions]);

  const totalPages = Math.ceil(totalItems / filters.limit);

  return {
    executions,
    isLoading,
    error,
    filters,
    totalItems,
    totalPages,
    currentPage: filters.page,
    updateFilters,
    goToPage,
    retryExecution,
    refresh,
  };
};

export default useExecutions;
